import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import '../assets/css/styles.min.css';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Swal from 'sweetalert2';
import LoadingSpinner from '../components/LoadingSpinner';

const StudentMessage = () => {
    const [messages, setMessages] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [newReply, setNewReply] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [conversations, setConversations] = useState([]);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // Search and pagination states
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Get authenticated user data
    const [currentUser, setCurrentUser] = useState(() => {
        const userData = localStorage.getItem('user');
        return userData ? JSON.parse(userData) : null;
    });

    useEffect(() => {
        if (!currentUser) {
            navigate('/login');
            return;
        }
        fetchConversations();
    }, [navigate, currentUser]);

    const handleApiError = (error, defaultMessage) => {
        console.error(error);
        setIsLoading(false);
        let errorMessage = defaultMessage;
        
        if (error.response && error.response.data) {
            errorMessage = error.response.data.error || 
                         error.response.data.message || 
                         JSON.stringify(error.response.data);
        } else if (error.message) {
            errorMessage = error.message;
        }

        Swal.fire('Error!', errorMessage, 'error');
        setError(errorMessage);
    };

    const fetchConversations = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
            const response = await fetch('https://api.codecircle.co/api/conversations', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            setConversations(data);
        } catch (error) {
            handleApiError(error, 'Failed to fetch conversations.');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchMessages = async (conversationId) => {
        setIsLoading(true);
        setError(null);
        
        try {
            const response = await fetch(`https://api.codecircle.co/api/conversations/${conversationId}/messages`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            setMessages(data);
        } catch (error) {
            handleApiError(error, 'Failed to fetch messages.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleConversationSelect = (conversation) => {
        setSelectedConversation(conversation);
        fetchMessages(conversation.id);
    };

    const handleReplySubmit = async (e) => {
        e.preventDefault();
        if (!newReply.trim() || !currentUser || !selectedConversation) return;

        setIsLoading(true);
        setError(null);
        
        try {
            const response = await fetch(
                `https://api.codecircle.co/api/conversations/${selectedConversation.id}/messages`, 
                {
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({
                        message: newReply,
                        user_id: currentUser.id,
                        is_admin: currentUser.role === 'admin' || currentUser.role === 'super_admin'
                    }),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || data.message || 'Failed to send message');
            }

            // Update messages list
            setMessages(prev => [...prev, data]);
            setNewReply('');
            
            // Update conversation in the list
            setConversations(prev => prev.map(conv => 
                conv.id === selectedConversation.id ? { 
                    ...conv, 
                    last_message: data.content,
                    updated_at: new Date().toISOString(),
                    read: currentUser.role === 'admin' || currentUser.role === 'super_admin'
                } : conv
            ));
            
            // Update selected conversation
            setSelectedConversation(prev => ({
                ...prev,
                last_message: data.content,
                updated_at: new Date().toISOString(),
                read: currentUser.role === 'admin' || currentUser.role === 'super_admin'
            }));

            Swal.fire({
                position: 'top-end',
                icon: 'success',
                title: 'Message sent!',
                showConfirmButton: false,
                timer: 1500
            });

        } catch (error) {
            console.error('Error sending message:', error);
            Swal.fire({
                icon: 'error',
                title: 'Failed to send message',
                text: error.message.includes('User not found') 
                    ? 'User account not found. Please contact support.'
                    : error.message
            });
        } finally {
            setIsLoading(false);
        }
    };

    const markAsRead = async (conversationId) => {
        try {
            const response = await fetch(
                `https://api.codecircle.co/api/conversations/${conversationId}/read`, 
                {
                    method: 'PUT',
                    headers: { 
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            // Update conversation in the list
            setConversations(prev => prev.map(conv => 
                conv.id === conversationId ? { ...conv, read: true } : conv
            ));
            
            // Update selected conversation if it's the current one
            setSelectedConversation(prev => 
                prev && prev.id === conversationId ? { ...prev, read: true } : prev
            );
            
            Swal.fire({
                position: 'top-end',
                icon: 'success',
                title: 'Marked as read',
                showConfirmButton: false,
                timer: 1500
            });
        } catch (error) {
            handleApiError(error, 'Failed to mark as read.');
        }
    };

    
    const deleteAllConversations = async () => {
       
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "All conversations and messages will be permanently deleted!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText:'Yes, delete all!',
            cancelButtonText: 'cancel'
        });
        
        
        if (result.isConfirmed) {
            setIsLoading(true);
            setError(null);
            
            try {
                const response = await fetch('https://api.codecircle.co/api/conversations/delete-all', {
                    method: 'DELETE',
                    headers: { 
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                
                const data = await response.json();
                
              
                setConversations([]);
                setMessages([]);
                setSelectedConversation(null);
                
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'All conversations and messages have been successfully deleted.',
                    confirmButtonText: 'حسناً'
                });
            } catch (error) {
                handleApiError(error, 'Failed to delete conversations.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    // Filter and pagination logic
    const filteredConversations = conversations.filter(conversation =>
        conversation.student_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conversation.last_message?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentConversations = filteredConversations.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredConversations.length / itemsPerPage);

    if (isLoading && !selectedConversation) {
        return (
            <div className="page-wrapper">
                <Sidebar />
                <div className="body-wrapper">
                    <Header />
                    <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 70px)' }}>
                        <LoadingSpinner />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper" id="main-wrapper" data-layout="vertical" data-navbarbg="skin6" data-sidebartype="full" data-sidebar-position="fixed" data-header-position="fixed">
            <Sidebar />
            <div className="body-wrapper">
                <Header />
                <div className="container-fluid">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="card-title fw-semibold">
                                    {currentUser?.role === 'admin' || currentUser?.role === 'super_admin' 
                                        ? 'Student Messages' 
                                        : 'My Messages'}
                                </h5>
                                
                               
                                {(currentUser?.role === 'admin' || currentUser?.role === 'super_admin') && (
                                    <button 
                                        className="btn btn-danger"
                                        onClick={deleteAllConversations}
                                        disabled={isLoading || conversations.length === 0}
                                    >
                                        <i className="fas fa-trash-alt me-1"></i>Delete all conversations
                                    </button>
                                )}
                            </div>
                            
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    <strong>Error:</strong> {error}
                                    <button type="button" className="btn-close float-end" onClick={() => setError(null)}></button>
                                </div>
                            )}
                            
                            <div className="row">
                                {/* Left sidebar - Conversation list */}
                                <div className="col-md-4 col-lg-3 border-end">
                                    <div className="d-flex mb-3">
                                        <div className="search-box flex-grow-1">
                                            <div className="input-group">
                                                <span className="input-group-text border-primary bg-primary">
                                                    <i className="fas fa-search text-white"></i>
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control border-primary"
                                                    placeholder="Search messages..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="conversation-list">
                                        {currentConversations.length > 0 ? (
                                            currentConversations.map((conversation) => (
                                                <div 
                                                    key={conversation.id} 
                                                    className={`p-3 border-bottom d-flex align-items-center ${selectedConversation?.id === conversation.id ? 'bg-light' : ''} ${!conversation.read ? 'fw-bold' : ''}`}
                                                    onClick={() => handleConversationSelect(conversation)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="flex-grow-1">
                                                        <h6 className="mb-0">{conversation.student_name}</h6>
                                                        <small className="text-muted">
                                                            {conversation.last_message?.substring(0, 50)}
                                                            {conversation.last_message?.length > 50 ? '...' : ''}
                                                        </small>
                                                        <div>
                                                            <small className="text-muted">
                                                                {new Date(conversation.updated_at).toLocaleString()}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    {!conversation.read && (
                                                        <span className="badge bg-primary rounded-pill">New</span>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-center py-4 text-muted">No conversations found</p>
                                        )}
                                    </div>
                                    
                                    {/* Pagination Controls */}
                                    {filteredConversations.length > itemsPerPage && (
                                        <div className="d-flex justify-content-center mt-3">
                                            <nav>
                                                <ul className="pagination pagination-sm pagination-primary mb-0">
                                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                        <button 
                                                            className="page-link" 
                                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                        >
                                                            <i className="fas fa-chevron-left"></i>
                                                        </button>
                                                    </li>
                                                    {[...Array(totalPages)].map((_, i) => (
                                                        <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                                            <button 
                                                                className="page-link"
                                                                onClick={() => setCurrentPage(i + 1)}
                                                            >
                                                                {i + 1}
                                                            </button>
                                                        </li>
                                                    ))}
                                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                        <button 
                                                            className="page-link"
                                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                        >
                                                            <i className="fas fa-chevron-right"></i>
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Right message area */}
                                <div className="col-md-8 col-lg-9">
                                    {selectedConversation ? (
                                        <>
                                            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                                                <div>
                                                    <h5 className="mb-0">{selectedConversation.student_name}</h5>
                                                    <small className="text-muted">Student ID: {selectedConversation.student_id}</small>
                                                </div>
                                                {(currentUser?.role === 'admin' || currentUser?.role === 'super_admin') && (
                                                    <div className="form-check">
                                                        <input 
                                                            className="form-check-input" 
                                                            type="checkbox" 
                                                            id="markAsRead" 
                                                            checked={selectedConversation.read} 
                                                            onChange={() => markAsRead(selectedConversation.id)}
                                                        />
                                                        <label className="form-check-label" htmlFor="markAsRead">
                                                            Mark as read
                                                        </label>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div className="message-area p-3" style={{ height: '400px', overflowY: 'auto' }}>
                                                {messages.length > 0 ? (
                                                    messages.map((message) => (
                                                        <div key={message.id} className={`mb-3 ${message.is_admin ? 'text-end' : ''}`}>
                                                            <div className={`d-inline-block p-3 rounded-3 ${message.is_admin ? 'bg-primary text-white' : 'bg-light'}`} style={{ maxWidth: '80%' }}>
                                                                <div className="message-content">{message.content}</div>
                                                                <div className="message-time mt-1">
                                                                    <small className={message.is_admin ? 'text-white-50' : 'text-muted'}>
                                                                        {new Date(message.created_at).toLocaleString()}
                                                                        {message.is_admin ? ' (Admin)' : ' (You)'}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="text-center py-4 text-muted">
                                                        <p>No messages yet. Start the conversation!</p>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div className="reply-area p-3 border-top">
                                                <form onSubmit={handleReplySubmit}>
                                                    <div className="input-group">
                                                        <textarea 
                                                            className="form-control" 
                                                            placeholder="Type your reply..." 
                                                            value={newReply}
                                                            onChange={(e) => setNewReply(e.target.value)}
                                                            rows="3"
                                                            disabled={isLoading}
                                                        ></textarea>
                                                        <button 
                                                            className="btn btn-primary" 
                                                            type="submit" 
                                                            disabled={!newReply.trim() || isLoading}
                                                        >
                                                            {isLoading ? (
                                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                            ) : (
                                                                <i className="fas fa-paper-plane me-1"></i>
                                                            )} 
                                                            Send
                                                        </button>
                                                    </div>
                                                </form>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="d-flex justify-content-center align-items-center h-100">
                                            <div className="text-center text-muted">
                                                <i className="fas fa-comments fa-4x mb-3"></i>
                                                <h5>Select a conversation to view messages</h5>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentMessage;